import { useCallback, useState } from "react";

import { useAddressBook } from "~/features/address-book/hooks/use-address-book.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";

type SaveLegacyConfirmationState = {
	isOpen: boolean;
	dataItem: AddressBookItemSummary | null;
};

export function useAddressLegacySaving() {
	const { createAddressBookItem } = useAddressBook();

	const [saveLegacyConfirmation, setSaveLegacyConfirmation] =
		useState<SaveLegacyConfirmationState>({
			isOpen: false,
			dataItem: null,
		});

	const openSaveLegacyConfirmation = useCallback(
		(dataItem: AddressBookItemSummary) => {
			setSaveLegacyConfirmation({
				isOpen: true,
				dataItem,
			});
		},
		[],
	);

	const closeSaveLegacyConfirmation = useCallback(() => {
		setSaveLegacyConfirmation({
			isOpen: false,
			dataItem: null,
		});
	}, []);

	const handleConfirmSaveLegacy = useCallback(async () => {
		if (saveLegacyConfirmation.dataItem) {
			const { dataItem } = saveLegacyConfirmation;
			await createAddressBookItem({
				customerId: dataItem.customerId,
				companyName: dataItem.companyName ?? "",
				address1: dataItem.address1,
				address2: dataItem.address2 ?? undefined,
				postalCode: dataItem.postalCode ?? "",
				city: dataItem.city ?? "",
				stateProvince: dataItem.stateProvince ?? "",
				country: dataItem.country,
				isValidated: dataItem.isValidated ?? undefined,
				legacyAddressId: dataItem.isLegacy
					? dataItem.addressBookItemId
					: (dataItem.legacyAddressId ?? undefined),
				details: dataItem.details.map((detail) => ({
					contactName: detail.contactName ?? undefined,
					contactPhone: detail.contactPhone ?? undefined,
					contactExtension: detail.contactExtension ?? undefined,
					hoursOpen: detail.hoursOpen ?? undefined,
					hoursClose: detail.hoursClose ?? undefined,
					stopType: detail.stopType,
					mode: detail.mode,
					timeZone: detail.timeZone ?? undefined,
					specialInstructions: detail.specialInstructions ?? "",
					accessorials: detail.accessorials ?? undefined,
				})),
			});
		}
		closeSaveLegacyConfirmation();
	}, [
		createAddressBookItem,
		saveLegacyConfirmation,
		closeSaveLegacyConfirmation,
	]);

	return {
		saveLegacyConfirmation,
		openSaveLegacyConfirmation,
		closeSaveLegacyConfirmation,
		handleConfirmSaveLegacy,
	};
}
